import { ChevronRight } from "lucide-react";

/**
 * Stat — small labelled numeric card used in the top summary strip on the
 * Tables view (Toplam, Aktif, Bağlı, vs).
 */
export const Stat = ({ label, value, hint, success, warning }) => (
    <div className="rounded-xl border border-border bg-surface p-4">
        <div className="text-[10px] font-semibold tracking-wider uppercase text-muted-foreground">{label}</div>
        <div className={`font-display text-2xl font-black tracking-tighter num mt-1 ${success ? "text-success" : warning ? "text-warning" : ""}`}>{value}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>
    </div>
);

/** Th — plain table header cell (no sort). */
export const Th = ({ children, className = "" }) => <th className={`text-left font-semibold px-4 py-3 ${className}`}>{children}</th>;

/** Td — plain table body cell (vertical-center + comfortable padding). */
export const Td = ({ children, className = "" }) => <td className={`px-4 py-3.5 align-middle ${className}`}>{children}</td>;

/**
 * SortableTh — table header cell with click-to-sort. Cycle: up → down → clear.
 *
 * Semantics differ per column and are documented on the sort logic in
 * `ProductsTable`:
 *   • name column: up = A→Z, down = Z→A
 *   • fit/model/image/table columns: up = "Var" üstte, down = "Yok" üstte
 *
 * `presenceMode` prop swaps the icon set so merchants get an unambiguous
 * visual for the presence-based sort. Default chevrons are used otherwise.
 */
export const SortableTh = ({ label, sortKey, state, onChange, presenceMode = false, className = "" }) => {
    const isActive = state?.key === sortKey;
    const dir = isActive ? state.dir : null;
    const cycle = () => {
        if (!isActive) return onChange({ key: sortKey, dir: "up" });
        if (state.dir === "up") return onChange({ key: sortKey, dir: "down" });
        return onChange({ key: null, dir: "up" }); // third click clears
    };
    const upActive = dir === "up";
    const downActive = dir === "down";
    const tooltip = presenceMode
        ? (isActive
            ? (upActive ? `${label} olanlar üstte · tekrar tıkla: olmayanlar üstte` : `${label} olmayanlar üstte · tekrar tıkla: sıralamayı temizle`)
            : `${label} olanlara göre sırala`)
        : (isActive
            ? (upActive ? "A → Z · tekrar tıkla: Z → A" : "Z → A · tekrar tıkla: sıralamayı temizle")
            : "Alfabetik sırala");
    return (
        <th className={`text-left font-semibold px-4 py-3 ${className}`}>
            <button
                type="button"
                onClick={cycle}
                title={tooltip}
                className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider transition-colors ${isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground/80"}`}
                data-testid={`sort-${sortKey}`}
                aria-label={tooltip}
                aria-sort={isActive ? (upActive ? "ascending" : "descending") : "none"}
            >
                <span>{label}</span>
                <span className="inline-flex flex-col leading-none">
                    <ChevronRight className={`h-2.5 w-2.5 -mb-0.5 rotate-[-90deg] transition-opacity ${upActive ? "opacity-100" : "opacity-25"}`} strokeWidth={2.5} />
                    <ChevronRight className={`h-2.5 w-2.5 rotate-90 transition-opacity ${downActive ? "opacity-100" : "opacity-25"}`} strokeWidth={2.5} />
                </span>
            </button>
        </th>
    );
};

/**
 * AssignPlaceholder — dashed-border "+ Ata" button used inside cells where the
 * merchant has NOT yet assigned a value. Compact, subtle, matches the row
 * height of the assigned state so the grid stays visually consistent.
 */
export const AssignPlaceholder = ({ icon: Icon, label, onClick, testId }) => (
    <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md border border-dashed border-border/70 text-muted-foreground/70 hover:text-foreground hover:border-foreground/40 hover:bg-muted/50 transition-colors text-[11px]"
        data-testid={testId}
    >
        {Icon && <Icon className="h-3 w-3" strokeWidth={1.75} />}
        <span>+ {label}</span>
    </button>
);
